/**
 * MilestoneCardTemplates — pure presentational cards for achievement milestones.
 *
 * Each template is a React component that accepts { badge, userName, previewW, previewH, theme }
 * and renders a shareable 9:16 (or 1:1) image. Used by MilestoneCardGenerator.
 *
 * Templates chosen based on badge.category:
 *   - workouts  → WorkoutMilestoneTemplate
 *   - volume    → VolumeMilestoneTemplate
 *   - streaks   → StreakMilestoneTemplate
 *   - prs       → PRMilestoneTemplate
 *   - (any)     → GenericMilestoneTemplate (fallback)
 */

import React from 'react';

const FONT = "'Barlow Condensed', sans-serif";

function LogoLockup({ size = 22, primary = '#FF5C00', secondary = '#ffffff' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.3, fontFamily: FONT }}>
      <div
        style={{
          width: size,
          height: size,
          borderRadius: size * 0.25,
          background: primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontWeight: 900,
          fontSize: size * 0.7,
        }}
      >
        RS
      </div>
      <span
        style={{
          fontSize: size * 0.7,
          fontWeight: 800,
          letterSpacing: size * 0.03,
          textTransform: 'uppercase',
          color: secondary,
        }}
      >
        trainedby<span style={{ color: primary }}>RS</span>
      </span>
    </div>
  );
}

function BaseCard({ previewW, previewH, children, gradient }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: gradient || 'linear-gradient(165deg, #1a0a00 0%, #0a0a0a 60%, #1a0a00 100%)',
        color: '#fff',
        fontFamily: FONT,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Decorative orbs */}
      <div
        style={{
          position: 'absolute',
          top: -previewW * 0.3,
          right: -previewW * 0.3,
          width: previewW * 0.8,
          height: previewW * 0.8,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,92,0,0.35) 0%, transparent 70%)',
          filter: 'blur(4px)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -previewH * 0.18,
          left: -previewW * 0.25,
          width: previewW * 0.7,
          height: previewW * 0.7,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,140,66,0.18) 0%, transparent 70%)',
          filter: 'blur(6px)',
        }}
      />
      {children}
    </div>
  );
}

function HeaderRow({ label, pad }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: pad,
      }}
    >
      <LogoLockup size={pad * 1.1} />
      <div
        style={{
          fontSize: pad * 0.48,
          fontWeight: 700,
          letterSpacing: pad * 0.1,
          textTransform: 'uppercase',
          color: '#FF5C00',
          padding: `${pad * 0.18}px ${pad * 0.5}px`,
          border: '1px solid rgba(255,92,0,0.5)',
          borderRadius: pad * 0.3,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function FooterRow({ userName, pad }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: pad,
        borderTop: '1px solid rgba(255,255,255,0.12)',
      }}
    >
      <span style={{ fontSize: pad * 0.5, color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
        trainedbyrs.com
      </span>
      {userName && (
        <span style={{ fontSize: pad * 0.5, color: 'rgba(255,255,255,0.6)' }}>
          @{userName}
        </span>
      )}
    </div>
  );
}

function BigIcon({ icon, size }) {
  return (
    <div
      style={{
        fontSize: size,
        lineHeight: 1,
        textAlign: 'center',
        filter: 'drop-shadow(0 0 30px rgba(255,92,0,0.6))',
      }}
    >
      {icon}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Template: Workout Milestone
// ──────────────────────────────────────────────────────────────────
function WorkoutMilestoneTemplate({ badge, userName, previewW, previewH }) {
  const pad = previewW * 0.06;
  return (
    <BaseCard previewW={previewW} previewH={previewH}>
      <HeaderRow label="Milestone" pad={pad} />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: pad,
          gap: pad * 0.6,
          zIndex: 1,
        }}
      >
        <BigIcon icon={badge.icon} size={previewW * 0.38} />
        <div
          style={{
            fontSize: previewW * 0.11,
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: previewW * 0.004,
            textAlign: 'center',
            lineHeight: 0.95,
          }}
        >
          {badge.title}
        </div>
        <div
          style={{
            fontSize: previewW * 0.05,
            color: 'rgba(255,255,255,0.85)',
            textAlign: 'center',
            maxWidth: '85%',
            lineHeight: 1.25,
          }}
        >
          {badge.description}
        </div>
        <div
          style={{
            marginTop: pad * 0.5,
            padding: `${pad * 0.4}px ${pad * 0.8}px`,
            borderRadius: pad * 0.4,
            background: '#FF5C00',
            color: '#fff',
            fontSize: previewW * 0.045,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: 1,
          }}
        >
          Join me on trainedbyRS
        </div>
      </div>
      <FooterRow userName={userName} pad={pad} />
    </BaseCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Template: Volume Milestone
// ──────────────────────────────────────────────────────────────────
function VolumeMilestoneTemplate({ badge, userName, previewW, previewH }) {
  const pad = previewW * 0.06;
  return (
    <BaseCard
      previewW={previewW}
      previewH={previewH}
      gradient="linear-gradient(165deg, #0a0a2a 0%, #0a0a0a 60%, #2a0a1a 100%)"
    >
      <HeaderRow label="Volume" pad={pad} />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: pad,
          gap: pad * 0.5,
          zIndex: 1,
        }}
      >
        <BigIcon icon={badge.icon} size={previewW * 0.32} />
        <div
          style={{
            fontSize: previewW * 0.18,
            fontWeight: 900,
            background: 'linear-gradient(90deg, #FF5C00, #FCD34D)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: 1,
          }}
        >
          {badge.title}
        </div>
        <div
          style={{
            fontSize: previewW * 0.06,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: previewW * 0.006,
            color: '#fff',
            textAlign: 'center',
          }}
        >
          Lifted
        </div>
        <div
          style={{
            fontSize: previewW * 0.048,
            color: 'rgba(255,255,255,0.8)',
            textAlign: 'center',
            maxWidth: '85%',
          }}
        >
          {badge.description}
        </div>
      </div>
      <FooterRow userName={userName} pad={pad} />
    </BaseCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Template: Streak Milestone
// ──────────────────────────────────────────────────────────────────
function StreakMilestoneTemplate({ badge, userName, previewW, previewH }) {
  const pad = previewW * 0.06;
  return (
    <BaseCard
      previewW={previewW}
      previewH={previewH}
      gradient="linear-gradient(165deg, #2a1a00 0%, #1a0a00 60%, #0a0000 100%)"
    >
      <HeaderRow label="Streak" pad={pad} />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: pad,
          gap: pad * 0.5,
          zIndex: 1,
        }}
      >
        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              inset: -pad,
              background: 'radial-gradient(circle, rgba(255,92,0,0.5) 0%, transparent 70%)',
              borderRadius: '50%',
            }}
          />
          <BigIcon icon={badge.icon} size={previewW * 0.36} />
        </div>
        <div
          style={{
            fontSize: previewW * 0.12,
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: previewW * 0.004,
            textAlign: 'center',
            color: '#fff',
            lineHeight: 0.95,
          }}
        >
          {badge.title}
        </div>
        <div
          style={{
            fontSize: previewW * 0.055,
            color: '#FF8C42',
            textAlign: 'center',
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          {badge.description}
        </div>
        <div
          style={{
            marginTop: pad * 0.3,
            fontSize: previewW * 0.04,
            color: 'rgba(255,255,255,0.6)',
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          Consistency wins.
        </div>
      </div>
      <FooterRow userName={userName} pad={pad} />
    </BaseCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Template: PR Milestone
// ──────────────────────────────────────────────────────────────────
function PRMilestoneTemplate({ badge, userName, previewW, previewH }) {
  const pad = previewW * 0.06;
  return (
    <BaseCard
      previewW={previewW}
      previewH={previewH}
      gradient="linear-gradient(165deg, #1a0a1a 0%, #0a0a0a 60%, #2a0a00 100%)"
    >
      <HeaderRow label="Personal Record" pad={pad} />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: pad,
          gap: pad * 0.5,
          zIndex: 1,
        }}
      >
        <BigIcon icon={badge.icon} size={previewW * 0.34} />
        <div
          style={{
            fontSize: previewW * 0.11,
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: previewW * 0.005,
            textAlign: 'center',
            color: '#fff',
            lineHeight: 0.95,
          }}
        >
          {badge.title}
        </div>
        <div
          style={{
            fontSize: previewW * 0.05,
            color: 'rgba(255,255,255,0.8)',
            textAlign: 'center',
            maxWidth: '85%',
          }}
        >
          {badge.description}
        </div>
      </div>
      <FooterRow userName={userName} pad={pad} />
    </BaseCard>
  );
}

// ──────────────────────────────────────────────────────────────────
// Fallback / Generic template
// ──────────────────────────────────────────────────────────────────
function GenericMilestoneTemplate({ badge, userName, previewW, previewH }) {
  const pad = previewW * 0.06;
  return (
    <BaseCard previewW={previewW} previewH={previewH}>
      <HeaderRow label="Unlocked" pad={pad} />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: pad,
          gap: pad * 0.5,
          zIndex: 1,
        }}
      >
        <BigIcon icon={badge.icon} size={previewW * 0.36} />
        <div
          style={{
            fontSize: previewW * 0.1,
            fontWeight: 900,
            textTransform: 'uppercase',
            textAlign: 'center',
            color: '#fff',
            lineHeight: 1,
          }}
        >
          {badge.title}
        </div>
        <div
          style={{
            fontSize: previewW * 0.048,
            color: 'rgba(255,255,255,0.8)',
            textAlign: 'center',
            maxWidth: '85%',
            lineHeight: 1.3,
          }}
        >
          {badge.description}
        </div>
      </div>
      <FooterRow userName={userName} pad={pad} />
    </BaseCard>
  );
}

export const MILESTONE_TEMPLATES = {
  workouts: WorkoutMilestoneTemplate,
  volume: VolumeMilestoneTemplate,
  streaks: StreakMilestoneTemplate,
  prs: PRMilestoneTemplate,
  generic: GenericMilestoneTemplate,
};

export function templateFor(badge) {
  return MILESTONE_TEMPLATES[badge?.category] || MILESTONE_TEMPLATES.generic;
}
